import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { realLinkProps } from '../components/links/realLinkProps';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import XeroImportPanel from '../components/XeroImportPanel';
import { xeroImportState, xeroReferenceFor } from '../lib/xeroImport';
import Instructions from '../components/Instructions';
import BackToWork from '../components/BackToWork';
import JournalSourcePicker from '../components/JournalSourcePicker';
import JournalAttachments from '../components/JournalAttachments';
import { figuresFromFields } from '../lib/posRegistry';
import { useMoment } from '../components/moments/useMoment';
import { dr15InXeroMoment, firstJournalMoment } from '../components/moments/conversionMoments';
import { formatMissingRoles } from '../lib/glRoleLabels';
import {
  ExportError,
  useCreateJournal,
  useExportJournal,
  useCorrectJournal,
  useJournalTemplates,
  useJournals,
  useClient,
  useProposeJournal,
  useUpdateJournal,
  useExtractionRun,
  useClientGlMap,
} from '../services/conversionsApi';
import { computeTotals, formatMoney as money, parseAmount } from '../lib/journalMath';
import type {
  PosReportReadResult,
  Dr15Extract,
  Dr15ExtractionResult,
  JournalLine,
  ManualJournal,
  PosPeriodTotals,
  SalesSplit,
  UploadedDocument,
} from '../types';
import { describeSplit, splitLearnedOn } from '../lib/glChart';
import {
  lineFiguresOf,
  lineFormFrom,
  lineLabel,
  migratePosFigures,
  journalSalesTotal,
  salesTotal,
  difference,
  ownTemplate,
  typedOnPosJournal,
  type LineForm,
} from '../lib/clientTemplate';
import { CalendarDateInput } from '../components/dates/CalendarDateInput';
import { usePageStyles } from '../components/layout/pageStyles';

const useStyles = makeStyles({
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  sectionTitle: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold },
  listHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: tokens.spacingHorizontalM },
  row: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  amountCell: { textAlign: 'right' },
  narrowInput: { width: '120px' },
  // The running readout. A bookkeeper should never discover an imbalance from a 400,
  // so this is always on screen while lines are being edited (spec §5.5).
  totals: {
    display: 'flex',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    flexWrap: 'wrap',
  },
  totalItem: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  totalLabel: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  totalValue: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold },
  balanced: { color: tokens.colorPaletteGreenForeground1 },
  unbalanced: { color: tokens.colorPaletteRedForeground1 },
});

/** Blank line — the shape the model wants: one of debit/credit, never both. */
const emptyLine = (): JournalLine => ({
  accountCode: '',
  accountName: '',
  description: '',
  debit: null,
  credit: null,
});

/**
 * Amounts are held as STRINGS while editing. Parsing on every keystroke would fight
 * the user mid-entry ("1." is not a number yet, and coercing it to 1 moves the caret);
 * the model's number is derived at the boundary instead.
 */
type LineDraft = Omit<JournalLine, 'debit' | 'credit'> & { debit: string; credit: string };

const toDraft = (line: JournalLine): LineDraft => ({
  ...line,
  accountName: line.accountName ?? '',
  description: line.description ?? '',
  debit: line.debit == null ? '' : String(line.debit),
  credit: line.credit == null ? '' : String(line.credit),
});

const toLine = (draft: LineDraft): JournalLine => ({
  accountCode: draft.accountCode.trim(),
  accountName: draft.accountName?.trim() || null,
  description: draft.description?.trim() || null,
  debit: parseAmount(draft.debit),
  credit: parseAmount(draft.credit),
});

/**
 * The journal date when nothing better is known: the last day of LAST month, in the person's own zone. A DR-15 is filed
 * for the month before, so this is usually right, and the screen says to check it. Never blank (Christian, 2026-10-27).
 */
function lastDayOfPreviousMonth(now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Where the journal date on screen came from, for the hint under it. */
type DateSource = 'return' | 'default' | 'typed' | 'journal';

/** The POS template's figure boxes, in the order the screen shows them. */
const POS_FIELDS = [
  'netSales',
  'salesTax',
  'tips',
  'cardSettlement',
  'cash',
  'merchantFees',
  'collectionAllowance',
] as const satisfies readonly (keyof PosPeriodTotals)[];
type PosForm = Record<(typeof POS_FIELDS)[number], string>;

const EMPTY_POS: PosForm = {
  netSales: '',
  salesTax: '',
  tips: '',
  cardSettlement: '',
  cash: '',
  merchantFees: '',
  collectionAllowance: '',
};

/** A stored figure the bookkeeper left blank stays EMPTY, never "0" (the same rule as the DR-15 form). */
const posFormFrom = (figures: PosPeriodTotals | null | undefined): PosForm =>
  Object.fromEntries(POS_FIELDS.map((f) => [f, figures?.[f] == null ? '' : String(figures[f])])) as PosForm;

/**
 * The boxes as a save sends them (Christian, 2026-10-29: they used to live only in the screen, so Save dropped them).
 * Null when every box is blank, so an untouched form stores nothing.
 */
const posFiguresOf = (form: PosForm): PosPeriodTotals | null => {
  const figures = Object.fromEntries(POS_FIELDS.map((f) => [f, parseAmount(form[f])])) as PosPeriodTotals;
  return POS_FIELDS.some((f) => figures[f] != null) ? figures : null;
};

/** The fields a save sends, normalized the same way on both sides, so "unsaved" means a real difference. */
const editSnapshot = (
  journalDate: string,
  narration: string,
  lines: LineDraft[],
  posFigures: PosPeriodTotals | null,
  lineFigures: Record<string, number | null> | null,
) => JSON.stringify({ journalDate, narration, lines: lines.map(toLine), posFigures, lineFigures });

/** Keys in a stable order, so two equal figure sets compare equal whatever order they were built in. */
const sortedFigures = (figures: Record<string, number | null> | null | undefined): Record<string, number | null> | null =>
  figures ? Object.fromEntries(Object.entries(figures).sort(([a], [b]) => a.localeCompare(b))) : null;

/** The §9 / B1 handoff params: one upload's, for one journal. Dropped on Start new and on a client change. */
const HANDOFF_PARAMS = ['sourceDocumentId', 'template', 'extractionRunId'] as const;

/** The DR-15 figures `salesTaxDr15` reads (api JournalTemplateService), in the order a bookkeeper reads the return. */
const DR15_FIELDS = ['taxCollected', 'collectionAllowance', 'penalty', 'interest', 'totalDue'] as const;
type Dr15Field = (typeof DR15_FIELDS)[number];

const EMPTY_DR15: Record<Dr15Field, string> = {
  taxCollected: '',
  collectionAllowance: '',
  penalty: '',
  interest: '',
  totalDue: '',
};

/** An extracted figure the extractor did not return stays EMPTY, never "0": a blank is a question, a 0 is an answer. */
const dr15FormFrom = (extract: Partial<Dr15Extract>): Record<Dr15Field, string> =>
  Object.fromEntries(
    DR15_FIELDS.map((f) => [f, extract[f] == null ? '' : String(extract[f])]),
  ) as Record<Dr15Field, string>;

export default function JournalPage() {
  const styles = useStyles();
  const frame = usePageStyles();
  const { t, i18n } = useTranslation();

  // §9: one client context per page, carried in the URL so the screen is linkable and
  // survives a refresh. This also makes the upload → manual-journal handoff work: the
  // flow screens navigate here with `?clientId=`, and before C7 that param was ignored
  // because the client lived in component state — the handoff would have silently landed
  // on an empty picker.
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') ?? '';
  // The client is picked in the HEADER (docs/client-context.md). Changing it there resets every other param, so a
  // handoff, which belongs to one client's upload, never follows the bookkeeper to the next client.
  const dropHandoffParams = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    for (const key of HANDOFF_PARAMS) params.delete(key);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // ── The §9 handoff params ───────────────────────────────────────────────────
  //
  // §9 wants an upload handoff to land on "new, template pre-applied, client pre-set,
  // source document linked". These are the other two thirds of that.
  //
  // ⚠ They are read AND honoured. C7 deliberately shipped without them rather than emit
  // params nothing consumed, because a URL carrying params the target ignores is a promise
  // the app does not keep — it looks delivered until someone checks. Anything added here
  // must stay wired at BOTH ends.
  const sourceDocumentId = searchParams.get('sourceDocumentId');
  const templateParam = searchParams.get('template');
  // B1 (docs/core-workflow.md): the DR-15 extraction whose figures fill the form. Wired at both ends, like the others.
  const extractionRunId = searchParams.get('extractionRunId');
  // The open journal is in the URL (docs/work-history.md §3; the fleet view-state rule), so Back, a refresh and a
  // link all reopen it. Opening one from the list PUSHES it; a new journal's first save REPLACES the URL with it.
  const journalIdParam = searchParams.get('journalId');
  // "New journal" opens the creation workflow as `?new=1` (Christian's walk, 2026-10-28: the list is the page, and a
  // new journal is a step taken from it), so a refresh keeps it open and Back returns to the list.
  const newParam = searchParams.get('new') === '1';

  const [templateId, setTemplateId] = useState(templateParam ?? 'blank');
  const [journalDate, setJournalDate] = useState(() => lastDayOfPreviousMonth());
  const [dateSource, setDateSource] = useState<DateSource>('default');
  const [narration, setNarration] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(), emptyLine()].map(toDraft));
  const [pos, setPos] = useState<PosForm>(EMPTY_POS);
  // The client's own template's boxes (docs/client-journal-template.md §A5), and the figures it carries but does not
  // show as boxes (a sales-tax-return line's, migrated from the old boxes): kept by every save, never dropped.
  const [lineForm, setLineForm] = useState<LineForm>({});
  const [carried, setCarried] = useState<Record<string, number | null>>({});
  const [dr15, setDr15] = useState<Record<Dr15Field, string>>(EMPTY_DR15);
  // The extraction run the form's figures came from (prefill, or a loaded draft's record). Null when typed by hand.
  const [formRunId, setFormRunId] = useState<string | null>(null);
  // What the last proposal was built from, recorded on the journal when it is saved (B1).
  const [proposedFrom, setProposedFrom] = useState<{ runId: string | null; figures: Partial<Dr15Extract> } | null>(null);
  const [savedJournal, setSavedJournal] = useState<ManualJournal | null>(null);
  // Attachments removed on screen and not saved yet (wave 4 §5); forgotten when another journal opens.
  const [removedDocs, setRemovedDocs] = useState<string[]>([]);
  useEffect(() => {
    setRemovedDocs([]);
  }, [savedJournal?.id]);
  // The editor is open for a journal, a new one, or a handoff (which always describes a new one). Otherwise the page
  // is the list.
  const editorOpen =
    !!savedJournal || !!journalIdParam || newParam || HANDOFF_PARAMS.some((key) => searchParams.has(key));
  const [splitNote, setSplitNote] = useState<
    { kind: 'applied' | 'learned'; split: SalesSplit } | { kind: 'stale' } | null
  >(null);
  const [banner, setBanner] = useState<{ intent: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [missingRoles, setMissingRoles] = useState<string[]>([]);

  const templates = useJournalTemplates();
  const extractionRun = useExtractionRun(clientId, extractionRunId);

  // Prefill ONCE per run, so a re-render can never overwrite a bookkeeper's corrections.
  const prefilledFrom = useRef<string | null>(null);
  useEffect(() => {
    const data = extractionRun.data;
    if (!data || prefilledFrom.current === data.id) return;
    prefilledFrom.current = data.id;
    setDr15(dr15FormFrom(data.extract));
    setFormRunId(data.id);
    // The journal posts on the return's PERIOD END (Christian, 2026-10-27), unless the person already chose a date.
    const periodEnd = data.extract?.periodEnd;
    if (periodEnd) {
      setDateSource((source) => {
        if (source === 'default') {
          setJournalDate(periodEnd);
          return 'return';
        }
        return source;
      });
    }
  }, [extractionRun.data]);

  // Review of #78: the form must never outlive the journal or client it was filled for. A client change (picker or
  // navigation) clears it, and blocks a late-arriving run from refilling it.
  const formClient = useRef(clientId);
  useEffect(() => {
    if (formClient.current === clientId) return;
    formClient.current = clientId;
    prefilledFrom.current = extractionRunId;
    setDr15(EMPTY_DR15);
    setPos(EMPTY_POS);
    lineFormFor.current = null;
    setFormRunId(null);
    setProposedFrom(null);
  }, [clientId, extractionRunId]);
  const journals = useJournals(clientId);
  const client = useClient(clientId);
  const glMap = useClientGlMap(clientId);
  const ownLines = ownTemplate(glMap.data?.journalTemplate);

  // Accomplishment moments (docs/accomplishment-moments.md) fire on the person's ACTION, never on opening a record
  // that already satisfies them. The detectors are pure; a stale or failed list means no moment.
  const [momentAction, setMomentAction] = useState<{ kind: 'confirmed' | 'exported'; journal: ManualJournal } | null>(null);
  const moment = useMemo(() => {
    if (!momentAction) return null;
    if (momentAction.kind === 'confirmed') return dr15InXeroMoment(momentAction.journal, t);
    return firstJournalMoment(momentAction.journal, journals.isError ? undefined : journals.data, client.data?.name, t);
  }, [momentAction, journals.data, journals.isError, client.data, t]);
  useMoment(moment);
  const propose = useProposeJournal();
  const create = useCreateJournal();
  const update = useUpdateJournal();
  const exportJournal = useExportJournal();
  const correctJournal = useCorrectJournal();

  // ── The live readout (spec §5.5) ───────────────────────────────────────────
  const totals = useMemo(() => computeTotals(lines.map(toLine)), [lines]);
  const { isBalanced, hasAmounts } = totals;
  const isFrozen = savedJournal?.status === 'exported';
  // The journal's attachments (wave 4 §5): what it has saved plus one just linked, less any removed on screen. A removed one
  // leaves the journal at the next save; the upload itself is untouched, and attaching it again puts it back.
  const attachedDocs = Array.from(new Set([...(savedJournal?.sourceDocumentIds ?? []), ...(sourceDocumentId ? [sourceDocumentId] : [])]))
    .filter((id) => !removedDocs.includes(id));
  // A report is offered only while the journal cites no source: none linked, none saved, or every one removed.
  const citesSource = attachedDocs.length > 0;
  // Accuracy Q3 (docs/accuracy-per-client.md): a journal proposed from an extraction started as the extractor's figures,
  // so it counts toward the client's streak only once the person says they checked them. Their statement, never inferred.
  const [figuresChecked, setFiguresChecked] = useState(false);
  const offerFiguresCheck = !!savedJournal?.proposedFromExtractionRunId && !isFrozen;
  // UNSAVED EDITS (Christian, 2026-10-27): Export sends the journal the SERVER holds, so an edit that is not saved yet
  // must never look exportable. Export is disabled while anything differs or a save is in flight, and a saved draft
  // saves itself when the person leaves a field.
  const isSaving = update.isPending || create.isPending;
  // A POS journal on the client's OWN template sends its line figures, and leaves the old boxes exactly as saved (§A7);
  // on the default template it sends the boxes. Any other template sends neither.
  const onOwnTemplate = templateId === 'posSalesPeriod' && ownLines !== null;
  const typedLines = (ownLines ?? []).filter(typedOnPosJournal);
  const returnLines = (ownLines ?? []).filter((l) => !typedOnPosJournal(l));
  const roleName = (key: string) => t(`glMappingPage.fields.${key}`, { defaultValue: key });
  const labelOfLine = (line: (typeof typedLines)[number]) => lineLabel(line, glMap.data?.rowLabels, roleName);
  const savedPosFigures = savedJournal?.templateId === 'posSalesPeriod' ? posFiguresOf(posFormFrom(savedJournal.posFigures)) : null;
  const posFigures = onOwnTemplate ? savedPosFigures : templateId === 'posSalesPeriod' ? posFiguresOf(pos) : null;
  const lineFigures = onOwnTemplate
    ? sortedFigures(lineFiguresOf(lineForm, parseAmount, carried))
    : sortedFigures(savedJournal?.lineFigures);
  // THE SALES CHECK (ruled 2026-10-30, docs/client-journal-template.md §A9): the sales the journal holds NOW, against the
  // total it had BEFORE (the journal this one corrects, else a migrated draft's own old Net sales box) and the POS
  // REPORT's total. Each difference is shown; nothing is held aside or left uncompared.
  const salesNow = templateId !== 'posSalesPeriod'
    ? null
    : onOwnTemplate
      ? salesTotal(typedLines, lineFiguresOf(lineForm, parseAmount))
      : parseAmount(pos.netSales);
  const corrected = savedJournal?.supersedesJournalId
    ? (journals.data ?? []).find((j) => j.id === savedJournal.supersedesJournalId) ?? null
    : null;
  const salesBefore: { kind: 'corrected' | 'oldBoxes'; total: number } | null = (() => {
    if (corrected) {
      const total = journalSalesTotal(corrected, ownLines);
      return total == null ? null : { kind: 'corrected', total };
    }
    const boxes = onOwnTemplate ? savedJournal?.posFigures?.netSales ?? null : null;
    return boxes == null ? null : { kind: 'oldBoxes', total: boxes };
  })();
  // The POS report's own total arrives with core's P2 (the engine's `sales.net` for the cited upload). Until then: not read.
  const salesReport: number | null = null;
  const showSalesCheck = templateId === 'posSalesPeriod' && (salesBefore !== null || citesSource);

  const savedSnapshot = savedJournal
    ? editSnapshot(
        savedJournal.journalDate,
        savedJournal.narration ?? '',
        savedJournal.lines.map(toDraft),
        savedPosFigures,
        sortedFigures(savedJournal.lineFigures),
      )
    : null;
  const isDirty =
    savedSnapshot !== null &&
    !isFrozen &&
    (editSnapshot(journalDate, narration, lines, posFigures, lineFigures) !== savedSnapshot
      || removedDocs.some((id) => savedJournal?.sourceDocumentIds?.includes(id)));
  // The template's boxes follow the journal on screen and the client's template, and nothing else: a save returns the
  // same journal, so it never resets what is being typed. A draft saved on the default boxes is MIGRATED here (§A7):
  // its figures go to the lines with the same keys; a box with no such line stays in posFigures, as the prior total the
  // sales check compares with (§A9). Nothing is written until the person saves.
  const lineFormFor = useRef<string | null>(null);
  const lineFormKey = (journalId: string | null) =>
    `${journalId ?? 'new'}|${ownLines ? ownLines.map((l) => l.key).join(',') : 'default'}`;
  useEffect(() => {
    const key = lineFormKey(savedJournal?.id ?? null);
    if (!ownLines || lineFormFor.current === key) return;
    lineFormFor.current = key;
    const figures = savedJournal?.lineFigures ?? migratePosFigures(savedJournal?.posFigures, ownLines);
    const shown = new Set(ownLines.filter(typedOnPosJournal).map((l) => l.key));
    setLineForm(lineFormFrom(ownLines.filter(typedOnPosJournal), figures));
    setCarried(Object.fromEntries(Object.entries(figures).filter(([k]) => !shown.has(k))));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the journal's id and the template's lines, by design
  }, [savedJournal, ownLines]);

  // A NEW journal is stored at its first auto-save once it has content (§4: nothing is lost between sessions). An empty
  // one is never created, so opening the screen and leaving stores nothing. `creating` holds off a second create while
  // the first is in flight, before its pending state has re-rendered.
  const creating = useRef(false);
  const hasContent =
    narration.trim() !== '' ||
    posFigures !== null ||
    lineFigures !== null ||
    lines.some((l) => l.accountCode.trim() !== '' || l.debit.trim() !== '' || l.credit.trim() !== '');
  const handleAutoSave = () => {
    if (isSaving) return;
    if (isDirty) handleSave(true);
    else if (!savedJournal && clientId && hasContent && !creating.current) handleSave(true);
  };

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  /**
   * Typing a debit clears the credit and vice versa. The model allows exactly one, so
   * the form enforces it at entry rather than letting the server reject it later.
   */
  const setDebit = (index: number, value: string) =>
    updateLine(index, { debit: value, ...(value.trim() !== '' ? { credit: '' } : {}) });
  const setCredit = (index: number, value: string) =>
    updateLine(index, { credit: value, ...(value.trim() !== '' ? { debit: '' } : {}) });

  const addLine = () => setLines((prev) => [...prev, toDraft(emptyLine())]);
  const removeLine = (index: number) =>
    setLines((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));

  // ⚠ An unknown `?template=` must SAY SO rather than silently falling back to blank. The
  // URL would otherwise claim a template was pre-applied while the form shows an empty
  // journal — the same class of quiet lie as a param the target ignores, just one layer in.
  const templateParamIsUnknown =
    templateParam !== null &&
    templateParam !== 'blank' &&
    templates.data !== undefined &&
    !templates.data.some((tpl) => tpl.id === templateParam);

  const deltaText = (d: number | null) =>
    d === null
      ? t('journalPage.salesCheck.deltaUnknown')
      : d === 0
        ? t('journalPage.salesCheck.same')
        : t('journalPage.salesCheck.delta', { amount: `${d > 0 ? '+' : '−'}${money(Math.abs(d))}` });

  const resetBanners = () => {
    setBanner(null);
    setMissingRoles([]);
    setSplitNote(null);
  };

  const handlePropose = () => {
    resetBanners();
    const dr15Figures = Object.fromEntries(
      DR15_FIELDS.map((f) => [f, parseAmount(dr15[f])]),
    ) as Partial<Dr15Extract>;
    const request =
      onOwnTemplate
        ? { journalDate, lineFigures: lineFiguresOf(lineForm, parseAmount) ?? {} }
        : templateId === 'posSalesPeriod'
        ? {
            journalDate,
            pos: Object.fromEntries(
              Object.entries(pos).map(([k, v]) => [k, parseAmount(v)]),
            ) as PosPeriodTotals,
          }
        : templateId === 'salesTaxDr15'
          ? { journalDate, dr15: dr15Figures }
          : { journalDate };

    propose.mutate(
      { clientId, templateId, request },
      {
        onSuccess: (proposal) => {
          // A proposal REPLACES the draft lines: it is a starting point, and merging it
          // into half-edited rows would be unpredictable.
          setLines(proposal.lines.map(toDraft));
          setNarration(proposal.narration);
          setProposedFrom(
            templateId === 'salesTaxDr15'
              ? { runId: formRunId, figures: dr15Figures }
              : null,
          );
          if (proposal.journalDate) setJournalDate(proposal.journalDate);
          // A saved DRAFT is updated by the proposal, not detached: detaching orphaned it, and Save created a second
          // journal (for a correction, cut loose from its original; review of B3). Only an exported one detaches.
          if (savedJournal?.status === 'exported') setSavedJournal(null);
          if (proposal.warnings.length > 0) {
            setBanner({ intent: 'warning', text: proposal.warnings.join(' ') });
          }
          if (proposal.appliedSalesSplit) setSplitNote({ kind: 'applied', split: proposal.appliedSalesSplit });
          else if (proposal.salesSplitStale) setSplitNote({ kind: 'stale' });
        },
        onError: (error) => {
          if (error instanceof ExportError && error.status === 412) {
            setMissingRoles(error.missingRoles ?? []);
            setBanner({ intent: 'error', text: t('journalPage.errors.missingRoles') });
            return;
          }
          setBanner({ intent: 'error', text: error instanceof Error ? error.message : String(error) });
        },
      },
    );
  };

  /** `edits` carries a value set in the same event, before React has re-rendered with it (a picked date). */
  const handleSave = (auto = false, edits: { journalDate?: string } = {}) => {
    if (!auto) resetBanners();
    // The handed-off document is recorded on the journal, and therefore ends up on the
    // conversion record when this journal is exported (C6 passes journal.SourceDocumentIds
    // straight through). That is the whole point of the link: months later, "which upload
    // produced this journal?" is answerable from the client's Conversions tab.
    //
    // Union rather than overwrite — an existing journal may already carry documents, and a
    // second handoff must not silently drop them.
    // Removed on screen (wave 4 §5) means removed here: the journal keeps exactly the attachments the screen lists.
    const sourceDocumentIds = attachedDocs;

    const payload: Partial<ManualJournal> = {
      journalDate: edits.journalDate ?? journalDate,
      narration,
      lines: lines.map(toLine),
      templateId: templateId === 'blank' ? null : templateId,
      sourceDocumentIds,
      proposedFromExtractionRunId: templateId === 'salesTaxDr15' ? proposedFrom?.runId ?? null : null,
      proposedFigures: templateId === 'salesTaxDr15' ? proposedFrom?.figures ?? null : null,
      posFigures,
      lineFigures,
    };

    const onError = (error: unknown) =>
      setBanner({ intent: 'error', text: error instanceof Error ? error.message : String(error) });

    if (savedJournal) {
      update.mutate(
        { clientId, journalId: savedJournal.id, payload },
        {
          onSuccess: (journal) => {
            setSavedJournal(journal);
            if (!auto) setBanner({ intent: 'success', text: t('journalPage.saved') });
            if (journal.salesSplitLearned) setSplitNote({ kind: 'learned', split: journal.salesSplitLearned });
          },
          onError,
        },
      );
    } else {
      creating.current = true;
      create.mutate(
        { clientId, payload },
        {
          onSuccess: (journal) => {
            // The boxes were typed for THIS journal: its first save must not reset them (see the effect above).
            if (lineFormFor.current === lineFormKey(null)) lineFormFor.current = lineFormKey(journal.id);
            setSavedJournal(journal);
            if (!auto) setBanner({ intent: 'success', text: t('journalPage.saved') });
            if (journal.salesSplitLearned) setSplitNote({ kind: 'learned', split: journal.salesSplitLearned });
            const params = new URLSearchParams(searchParams);
            params.set('journalId', journal.id);
            params.delete('new');
            appliedJournalParam.current = journal.id;
            setSearchParams(params, { replace: true });
          },
          onError,
          onSettled: () => {
            creating.current = false;
          },
        },
      );
    }
  };

  const handleExport = () => {
    if (!savedJournal) return;
    resetBanners();
    exportJournal.mutate(
      { clientId, journalId: savedJournal.id, figuresChecked: offerFiguresCheck && figuresChecked },
      {
        onSuccess: (result) => {
          const url = URL.createObjectURL(result.blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = result.filename;
          anchor.click();
          URL.revokeObjectURL(url);
          // The server stamped this export's reference into the file (B2); show the same one.
          const exported: ManualJournal = { ...savedJournal, status: 'exported', xeroReference: savedJournal.xeroReference ?? xeroReferenceFor(savedJournal.id) };
          setSavedJournal(exported);
          setMomentAction({ kind: 'exported', journal: exported });
          setBanner({ intent: 'success', text: result.summary || t('journalPage.exported') });
        },
        onError: (error) =>
          setBanner({ intent: 'error', text: error instanceof Error ? error.message : String(error) }),
      },
    );
  };

  const loadDraft = (journal: ManualJournal) => {
    resetBanners();
    setSavedJournal(journal);
    setFiguresChecked(false);
    setJournalDate(journal.journalDate);
    setDateSource('journal');
    setNarration(journal.narration);
    setLines(journal.lines.map(toDraft));
    setTemplateId(journal.templateId ?? 'blank');
    setProposedFrom(
      journal.proposedFigures
        ? { runId: journal.proposedFromExtractionRunId ?? null, figures: journal.proposedFigures }
        : null,
    );
    prefilledFrom.current = extractionRunId;
    setDr15(journal.proposedFigures ? dr15FormFrom(journal.proposedFigures) : EMPTY_DR15);
    setFormRunId(journal.proposedFromExtractionRunId ?? null);
    setPos(posFormFrom(journal.posFigures));
    lineFormFor.current = null;
  };

  const startNew = () => {
    resetBanners();
    setSavedJournal(null);
    setFiguresChecked(false);
    setJournalDate(lastDayOfPreviousMonth());
    setDateSource('default');
    setNarration('');
    setLines([emptyLine(), emptyLine()].map(toDraft));
    prefilledFrom.current = extractionRunId;
    setDr15(EMPTY_DR15);
    setPos(EMPTY_POS);
    lineFormFor.current = null;
    setFormRunId(null);
    setProposedFrom(null);
    appliedJournalParam.current = null;
    dropHandoffParams();
  };

  /** Open a journal from the list: into the editor AND the URL (pushed, so Back returns to the one before). */
  /** The URL of this screen opened on one journal, or on a new one: a handoff describes a NEW journal, not this one. */
  const editorParams = (open: { journalId: string } | 'new') => {
    const params = new URLSearchParams(searchParams);
    for (const key of HANDOFF_PARAMS) params.delete(key);
    if (open === 'new') {
      params.delete('journalId');
      params.set('new', '1');
    } else {
      params.delete('new');
      params.set('journalId', open.journalId);
    }
    return params;
  };

  const openJournal = (journal: ManualJournal) => {
    if (isDirty && !isSaving) handleSave(true); // the one being left is saved first: nothing is lost by opening another
    loadDraft(journal);
    appliedJournalParam.current = journal.id;
    setSearchParams(editorParams({ journalId: journal.id }));
  };

  /** "New journal", at the head of the list: the one being left is saved first, then a fresh one, PUSHED as `new=1`. */
  const onNewJournal = () => {
    if (isDirty && !isSaving) handleSave(true);
    startNew();
    setSearchParams(editorParams('new'));
  };

  // Open and New journal are real links (fleet ruling 2026-10-29): right-click opens them in a new tab, where the URL
  // reopens the journal. A plain click stays in-page, saving the one being left first.
  const editorLink = (open: { journalId: string } | 'new', follow: () => void) =>
    realLinkProps(`/journals?${editorParams(open).toString()}`, { navigate: follow });

  /** A POS or payroll report attached from inside the journal (JournalSourcePicker): linked, cited at export. */
  const onReportAttached = (doc: UploadedDocument) => {
    setRemovedDocs((prev) => prev.filter((id) => id !== doc.id));
    const params = new URLSearchParams(searchParams);
    params.set('sourceDocumentId', doc.id);
    setSearchParams(params, { replace: true });
  };

  /** An attachment removed on screen (wave 4 §5): one just linked leaves the URL too. Saved with the journal. */
  const removeAttachment = (documentId: string) => {
    setRemovedDocs((prev) => [...prev, documentId]);
    if (documentId === sourceDocumentId) {
      const params = new URLSearchParams(searchParams);
      params.delete('sourceDocumentId');
      setSearchParams(params, { replace: true });
    }
  };

  /** A READ report's figures onto the client's template lines (P2.5): only lines whose every pick the report emitted. */
  const fillFromReport = (read: PosReportReadResult): string | null => {
    if (!onOwnTemplate) return t('journalPage.attachments.read.notOnTemplate');
    const { figures, filled, left } = figuresFromFields(typedLines, read.fields);
    setLineForm((prev) => ({ ...prev, ...figures }));
    const note = t('journalPage.attachments.read.filled', { count: filled, left });
    return note;
  };

  /** The DR-15 read from inside the journal (JournalSourcePicker): the B1 handoff params, onto THIS journal's URL. */
  const onDr15SourceRead = (doc: UploadedDocument, result: Dr15ExtractionResult) => {
    const params = new URLSearchParams(searchParams);
    params.set('sourceDocumentId', doc.id);
    params.set('template', 'salesTaxDr15');
    if (result.extractionRunId) params.set('extractionRunId', result.extractionRunId);
    setSearchParams(params, { replace: true });
  };

  // The URL opens a journal: a link, a refresh, or Back to an earlier one. Applied once per id, when the list has it.
  // An id the list does not have (removed, or not this client's) is said, and dropped from the URL.
  const appliedJournalParam = useRef<string | null>(null);
  const [journalNotFound, setJournalNotFound] = useState(false);
  useEffect(() => {
    if (journalIdParam === appliedJournalParam.current) return;
    if (!journalIdParam) {
      // Back to the URL with no journal: the editor follows it to a new journal (an edit is saved first).
      appliedJournalParam.current = null;
      if (savedJournal) {
        if (isDirty && !isSaving) handleSave(true);
        startNew();
      }
      return;
    }
    if (!journals.data) return;
    appliedJournalParam.current = journalIdParam;
    const found = journals.data.find((j) => j.id === journalIdParam);
    if (found) {
      if (isDirty && !isSaving && savedJournal?.id !== found.id) handleSave(true);
      setJournalNotFound(false);
      loadDraft(found);
    } else {
      setJournalNotFound(true);
      const params = new URLSearchParams(searchParams);
      params.delete('journalId');
      appliedJournalParam.current = null;
      setSearchParams(params, { replace: true });
    }
    // Only the URL and the list move this; the editor's own state must not re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalIdParam, journals.data]);

  return (
    <div className={frame.page}>
      {/* A visible way back to the client's Work list, filters kept (Christian's walk, 2026-10-28). */}
      {clientId && <BackToWork clientId={clientId} />}
      <div className={frame.header}>
        <Text as="h1" className={frame.title}>{t('journalPage.title')}</Text>
        <Text className={frame.subtitle}>{t('journalPage.subtitle')}</Text>
      </div>

      <Instructions screen="manualJournals" />

      {!clientId && (
        <MessageBar intent="info">
          <MessageBarBody>{t('journalPage.pickClientFirst')}</MessageBarBody>
        </MessageBar>
      )}

      {clientId && (
        <>
          {journalNotFound && (
            <MessageBar intent="warning">
              <MessageBarBody>{t('journalPage.journalNotFound')}</MessageBarBody>
            </MessageBar>
          )}
          {/* ── The journals: the page leads with them (Christian's walk, 2026-10-28) ── */}
          <div className={styles.section}>
            <div className={styles.listHead}>
              <Text className={styles.sectionTitle}>{t('journalPage.existingHeading')}</Text>
              <Button as="a" appearance="primary" icon={<AddRegular />} {...editorLink('new', onNewJournal)}>
                {t('journalPage.listNew')}
              </Button>
            </div>
            {journals.isLoading && <Spinner size="tiny" label={t('common.loading')} />}
            {journals.data?.length === 0 && (
              <Text className={styles.hint}>{t('journalPage.noJournals')}</Text>
            )}
            {journals.data && journals.data.length > 0 && (
              <Table size="small">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>{t('journalPage.columns.journalDate')}</TableHeaderCell>
                    <TableHeaderCell>{t('journalPage.columns.narration')}</TableHeaderCell>
                    <TableHeaderCell>{t('journalPage.columns.lines')}</TableHeaderCell>
                    <TableHeaderCell>{t('journalPage.columns.status')}</TableHeaderCell>
                    <TableHeaderCell />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {journals.data.map((journal) => (
                    <TableRow key={journal.id} aria-current={journal.id === savedJournal?.id ? 'true' : undefined}>
                      <TableCell>{journal.journalDate}</TableCell>
                      <TableCell>{journal.narration}</TableCell>
                      <TableCell>{journal.lines.length}</TableCell>
                      <TableCell>
                        <Badge
                          appearance="tint"
                          color={
                            xeroImportState(journal) === 'confirmed'
                              ? 'success'
                              : xeroImportState(journal) === 'unconfirmed'
                                ? 'warning'
                                : 'brand'
                          }
                        >
                          {journal.supersededByJournalId
                            ? t('journalPage.correct.listSuperseded')
                            : xeroImportState(journal) === 'confirmed'
                            ? t('journalPage.xero.listConfirmed')
                            : xeroImportState(journal) === 'unconfirmed'
                              ? t('journalPage.xero.listUnconfirmed')
                              : t(`journalPage.status.${journal.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button as="a" appearance="subtle" {...editorLink({ journalId: journal.id }, () => openJournal(journal))}>
                          {t('journalPage.open')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {editorOpen && (
            <>
          {/* ── Template ──────────────────────────────────────────────────── */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>{t('journalPage.templateHeading')}</Text>
            {templates.isLoading && <Spinner size="tiny" label={t('common.loading')} />}
            {templates.data && (
              <RadioGroup
                value={templateId}
                onChange={(_e, data) => {
                  setTemplateId(data.value);
                  resetBanners();
                }}
              >
                {templates.data.map((template) => (
                  <Radio
                    key={template.id}
                    value={template.id}
                    label={`${template.label} — ${template.description}`}
                  />
                ))}
              </RadioGroup>
            )}

            <JournalAttachments
              clientId={clientId}
              documentIds={attachedDocs}
              frozen={isFrozen}
              onRemove={removeAttachment}
              onFields={fillFromReport}
            />

            {templateId === 'blank' && !citesSource && !isFrozen && (
              <JournalSourcePicker clientId={clientId} category="payrollReport" onAttach={onReportAttached} />
            )}

            {templateId === 'posSalesPeriod' && !citesSource && !isFrozen && (
              <JournalSourcePicker clientId={clientId} category="posReport" onAttach={onReportAttached} />
            )}

            {templateId === 'posSalesPeriod' && glMap.isLoading && <Spinner size="tiny" label={t('common.loading')} />}

            {showSalesCheck && !glMap.isLoading && (
              <MessageBar
                intent={[difference(salesNow, salesBefore?.total ?? null), difference(salesNow, salesReport)].some((d) => d !== null && d !== 0) ? 'warning' : 'info'}
              >
                <MessageBarBody>
                  <Text weight="semibold">{t('journalPage.salesCheck.heading')}</Text>
                  <ul aria-label={t('journalPage.salesCheck.heading')}>
                    <li>{salesNow == null ? t('journalPage.salesCheck.nowNone') : t('journalPage.salesCheck.now', { amount: money(salesNow) })}</li>
                    {salesBefore && (
                      <li>
                        {t(`journalPage.salesCheck.before.${salesBefore.kind}`, { amount: money(salesBefore.total) })}
                        {' · '}
                        {deltaText(difference(salesNow, salesBefore.total))}
                      </li>
                    )}
                    <li>
                      {salesReport == null
                        ? t('journalPage.salesCheck.reportNotRead')
                        : `${t('journalPage.salesCheck.report', { amount: money(salesReport) })} · ${deltaText(difference(salesNow, salesReport))}`}
                    </li>
                  </ul>
                </MessageBarBody>
              </MessageBar>
            )}

            {onOwnTemplate && (
              <>
                <Text className={styles.hint}>{t('journalPage.clientTemplate.intro')}</Text>
                {!savedJournal?.lineFigures && savedJournal?.posFigures && (
                  <MessageBar intent="info">
                    <MessageBarBody>{t('journalPage.clientTemplate.migrated')}</MessageBarBody>
                  </MessageBar>
                )}
                {/* The client's own lines, in its order; leaving a box saves a saved draft, like every other field. */}
                <div className={styles.grid} onBlur={handleAutoSave}>
                  {typedLines.map((line) => (
                    <Field
                      key={line.key}
                      label={labelOfLine(line)}
                      hint={line.source === 'bank' ? t('journalPage.clientTemplate.fromBank') : undefined}
                    >
                      <Input
                        type="number"
                        step="0.01"
                        disabled={isFrozen}
                        value={lineForm[line.key] ?? ''}
                        onChange={(_e, data) => setLineForm((prev) => ({ ...prev, [line.key]: data.value }))}
                      />
                    </Field>
                  ))}
                </div>
                {returnLines.length > 0 && (
                  <div>
                    <Text weight="semibold">{t('journalPage.clientTemplate.returnHeading')}</Text>
                    <Text as="p" className={styles.hint}>{t('journalPage.clientTemplate.returnIntro')}</Text>
                    <ul>
                      {returnLines.map((line) => (
                        <li key={line.key}>
                          {carried[line.key] != null
                            ? t('journalPage.clientTemplate.returnLineCarried', { line: labelOfLine(line), amount: money(carried[line.key] as number) })
                            : labelOfLine(line)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {templateId === 'posSalesPeriod' && !onOwnTemplate && !glMap.isLoading && (
              // The boxes are saved with the journal, and leaving one saves a saved draft, like every other field.
              <div className={styles.grid} onBlur={handleAutoSave}>
                {POS_FIELDS.map((field) => (
                  <Field key={field} label={t(`journalPage.pos.${field}`)}>
                    <Input
                      type="number"
                      step="0.01"
                      disabled={isFrozen}
                      value={pos[field]}
                      onChange={(_e, data) => setPos((prev) => ({ ...prev, [field]: data.value }))}
                    />
                  </Field>
                ))}
              </div>
            )}

            {templateId === 'salesTaxDr15' && (
              <>
                {!extractionRunId && !formRunId && <JournalSourcePicker clientId={clientId} category="dr15Return" onRead={onDr15SourceRead} />}
                {extractionRunId && extractionRun.isLoading && (
                  <Spinner size="tiny" label={t('journalPage.dr15.loading')} />
                )}
                {extractionRunId && extractionRun.isError && (
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      {extractionRun.error instanceof Error ? extractionRun.error.message : t('journalPage.dr15.loadFailed')}
                    </MessageBarBody>
                  </MessageBar>
                )}
                {extractionRun.data && (
                  <MessageBar intent="info">
                    <MessageBarBody>
                      {t('journalPage.dr15.readFrom', { file: extractionRun.data.sourceFilename ?? '' })}
                    </MessageBarBody>
                  </MessageBar>
                )}
                {extractionRun.data?.crossCheckPassed === false && (
                  <MessageBar intent="warning">
                    <MessageBarBody>{t('journalPage.dr15.crossCheckFailed')}</MessageBarBody>
                  </MessageBar>
                )}
                <div className={styles.grid}>
                  {DR15_FIELDS.map((field) => (
                    <Field key={field} label={t(`journalPage.dr15.${field}`)}>
                      <Input
                        type="number"
                        step="0.01"
                        value={dr15[field]}
                        onChange={(_e, data) => setDr15((prev) => ({ ...prev, [field]: data.value }))}
                      />
                    </Field>
                  ))}
                </div>
              </>
            )}

            {templateId !== 'blank' && (
              <div className={styles.actions}>
                <Button
                  appearance="primary"
                  onClick={handlePropose}
                  disabled={propose.isPending}
                >
                  {propose.isPending ? t('journalPage.proposing') : t('journalPage.proposeLines')}
                </Button>
              </div>
            )}
          </div>

          {/* The handoff, made visible. A source document that is linked silently is a
              side effect nobody can confirm or correct — and it ends up on the exported
              conversion record, so it has to be reviewable before export, not after. */}
          {sourceDocumentId && (
            <MessageBar intent="info">
              <MessageBarBody>{t('journalPage.linkedSourceDocument')}</MessageBarBody>
            </MessageBar>
          )}
          {templateParamIsUnknown && (
            <MessageBar intent="warning">
              <MessageBarBody>{t('journalPage.unknownTemplateParam')}</MessageBarBody>
            </MessageBar>
          )}
          {banner && (
            <MessageBar intent={banner.intent}>
              <MessageBarBody>{banner.text}</MessageBarBody>
            </MessageBar>
          )}
          {/* The client's remembered sales split (docs/client-chart-and-sales-split.md §3): applied, stale, or just learned. */}
          {splitNote && (
            <MessageBar intent={splitNote.kind === 'stale' ? 'warning' : 'info'}>
              <MessageBarBody>
                {splitNote.kind === 'stale'
                  ? t('journalPage.salesSplit.stale')
                  : t(`journalPage.salesSplit.${splitNote.kind}`, {
                      shares: describeSplit(splitNote.split, i18n.language),
                      date: splitLearnedOn(splitNote.split, i18n.language),
                    })}
              </MessageBarBody>
            </MessageBar>
          )}

          {missingRoles.length > 0 && (
            <MessageBar intent="error">
              <MessageBarBody>
                {/*
                  These used to be joined raw — camelCase identifiers, in both languages,
                  shown to a bookkeeper. The translated labels for those very keys already
                  existed on the screen this message links to; see `lib/glRoleLabels.ts`
                  for why a direct lookup would have been worse than the identifiers.
                */}
                {t('journalPage.errors.missingRolesList', {
                  roles: formatMissingRoles(missingRoles, (key: string) => t(key)),
                })}{' '}
                <RouterLink to="/gl-mappings">{t('journalPage.errors.goToMappings')}</RouterLink>
              </MessageBarBody>
            </MessageBar>
          )}

          {/* ── Editor ────────────────────────────────────────────────────── */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>
              {t('journalPage.journalHeading')}{' '}
              {savedJournal && (
                <Badge appearance="tint" color={isFrozen ? 'informative' : 'brand'}>
                  {isFrozen ? t('journalPage.status.exported') : t('journalPage.status.draft')}
                </Badge>
              )}
            </Text>

            {isFrozen && (
              <MessageBar intent="info">
                <MessageBarBody>{t('journalPage.frozen')}</MessageBarBody>
              </MessageBar>
            )}
            {/* B2: the step after export. "Exported" is never "in Xero" until someone who can see Xero says so. */}
            {isFrozen && savedJournal && (
              <XeroImportPanel
                key={savedJournal.id}
                journal={savedJournal}
                onChange={(updated) => {
                  setSavedJournal(updated);
                  setMomentAction({ kind: 'confirmed', journal: updated });
                }}
              />
            )}
            {/* B3: a wrong exported journal is corrected, never edited or deleted. */}
            {isFrozen && savedJournal?.supersededByJournalId && (
              <MessageBar intent="warning">
                <MessageBarBody>{t('journalPage.correct.superseded')}</MessageBarBody>
              </MessageBar>
            )}
            {isFrozen && savedJournal && !savedJournal.supersededByJournalId && (
              <div className={styles.actions}>
                <Button
                  appearance="secondary"
                  disabled={correctJournal.isPending}
                  onClick={() =>
                    correctJournal.mutate(
                      { clientId, journalId: savedJournal.id },
                      {
                        onSuccess: (correction) => loadDraft(correction),
                        onError: (error) =>
                          setBanner({ intent: 'error', text: error instanceof Error ? error.message : String(error) }),
                      },
                    )
                  }
                >
                  {t('journalPage.correct.button')}
                </Button>
              </div>
            )}
            {!isFrozen && savedJournal?.supersedesJournalId && (
              <MessageBar intent="info">
                <MessageBarBody>{t('journalPage.correct.correcting', { date: savedJournal.journalDate })}</MessageBarBody>
              </MessageBar>
            )}

            {/* Auto-save on leaving any field of a saved draft: blur bubbles to here. */}
            <div onBlur={handleAutoSave}>
            <div className={styles.row}>
              <Field
                label={t('journalPage.fields.journalDate')}
                hint={
                  dateSource === 'return'
                    ? t('journalPage.fields.dateFromReturn')
                    : dateSource === 'default'
                      ? t('journalPage.fields.dateDefaulted')
                      : t('journalPage.fields.journalDateHint')
                }
              >
                {/* The fleet's own calendar (filter contract v2.3, C68): its month arrows never change the date. A
                    COMPLETE date, a day clicked or typed in full, is a deliberate choice, so a saved draft saves it at
                    once: a picked date is never left unsaved for want of a blur. */}
                <CalendarDateInput
                  value={journalDate}
                  disabled={isFrozen}
                  onChange={(value) => {
                    setJournalDate(value);
                    setDateSource('typed');
                    if (value && !isSaving && savedSnapshot !== null && !isFrozen &&
                        editSnapshot(value, narration, lines, posFigures, lineFigures) !== savedSnapshot) {
                      handleSave(true, { journalDate: value });
                    }
                  }}
                />
              </Field>
            </div>
            <Field label={t('journalPage.fields.narration')}>
              <Textarea
                value={narration}
                disabled={isFrozen}
                onChange={(_e, data) => setNarration(data.value)}
                resize="vertical"
              />
            </Field>

            <Table size="small">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('journalPage.columns.accountCode')}</TableHeaderCell>
                  <TableHeaderCell>{t('journalPage.columns.accountName')}</TableHeaderCell>
                  <TableHeaderCell>{t('journalPage.columns.description')}</TableHeaderCell>
                  <TableHeaderCell>{t('journalPage.columns.debit')}</TableHeaderCell>
                  <TableHeaderCell>{t('journalPage.columns.credit')}</TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, index) => (
                  // Index keys are correct here: lines are an ORDERED sequence with no
                  // stable id, and order is part of the exported CSV (spec §5.3).
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        value={line.accountCode}
                        disabled={isFrozen}
                        className={styles.narrowInput}
                        onChange={(_e, data) => updateLine(index, { accountCode: data.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={line.accountName ?? ''}
                        disabled={isFrozen}
                        onChange={(_e, data) => updateLine(index, { accountName: data.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={line.description ?? ''}
                        disabled={isFrozen}
                        onChange={(_e, data) => updateLine(index, { description: data.value })}
                      />
                    </TableCell>
                    <TableCell className={styles.amountCell}>
                      <Input
                        type="number"
                        step="0.01"
                        value={line.debit}
                        disabled={isFrozen}
                        className={styles.narrowInput}
                        onChange={(_e, data) => setDebit(index, data.value)}
                      />
                    </TableCell>
                    <TableCell className={styles.amountCell}>
                      <Input
                        type="number"
                        step="0.01"
                        value={line.credit}
                        disabled={isFrozen}
                        className={styles.narrowInput}
                        onChange={(_e, data) => setCredit(index, data.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        disabled={isFrozen || lines.length <= 2}
                        title={t('journalPage.removeLine')}
                        onClick={() => removeLine(index)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className={styles.actions}>
              <Button icon={<AddRegular />} disabled={isFrozen} onClick={addLine}>
                {t('journalPage.addLine')}
              </Button>
            </div>

            {/* The readout that makes an imbalance impossible to miss. */}
            <div className={styles.totals}>
              <div className={styles.totalItem}>
                <Text className={styles.totalLabel}>{t('journalPage.totals.debit')}</Text>
                <Text className={styles.totalValue}>{money(totals.totalDebit)}</Text>
              </div>
              <div className={styles.totalItem}>
                <Text className={styles.totalLabel}>{t('journalPage.totals.credit')}</Text>
                <Text className={styles.totalValue}>{money(totals.totalCredit)}</Text>
              </div>
              <div className={styles.totalItem}>
                <Text className={styles.totalLabel}>{t('journalPage.totals.difference')}</Text>
                <Text
                  className={`${styles.totalValue} ${isBalanced ? styles.balanced : styles.unbalanced}`}
                >
                  {money(totals.difference)}
                </Text>
              </div>
              {hasAmounts && (
                <Badge appearance="tint" color={isBalanced ? 'success' : 'danger'}>
                  {isBalanced ? t('journalPage.totals.balanced') : t('journalPage.totals.outOfBalance')}
                </Badge>
              )}
            </div>
            </div>

            {savedJournal && !isFrozen && (
              <Text size={200} aria-live="polite">
                {isDirty
                  ? t('journalPage.unsaved.unsaved')
                  : isSaving
                    ? t('journalPage.unsaved.saving')
                    : t('journalPage.unsaved.allSaved')}
              </Text>
            )}
            {isDirty && (
              <MessageBar intent="warning">
                <MessageBarBody>{t('journalPage.unsaved.exportBlocked')}</MessageBarBody>
              </MessageBar>
            )}
            {offerFiguresCheck && (
              <Checkbox
                checked={figuresChecked}
                onChange={(_e, data) => setFiguresChecked(data.checked === true)}
                label={t('journalPage.figuresCheck.label')}
              />
            )}
            {offerFiguresCheck && <Text className={styles.hint}>{t('journalPage.figuresCheck.hint')}</Text>}
            {isFrozen && savedJournal?.figuresConfirmedAt && (
              <Text className={styles.hint}>
                {t('journalPage.figuresCheck.recorded', { name: savedJournal.figuresConfirmedByName ?? t('uploads.removed.someone') })}
              </Text>
            )}
            <div className={styles.actions}>
              <Button
                onClick={() => handleSave()}
                disabled={isFrozen || create.isPending || update.isPending}
              >
                {savedJournal ? t('journalPage.saveChanges') : t('journalPage.saveDraft')}
              </Button>
              <Button
                appearance="primary"
                onClick={handleExport}
                // Export stays disabled while the journal does not balance. Discovering
                // an imbalance from a 400 is the experience §5.5 exists to prevent.
                disabled={!savedJournal || isFrozen || !isBalanced || exportJournal.isPending || isDirty || isSaving}
                title={!isBalanced ? t('journalPage.totals.outOfBalance') : undefined}
              >
                {exportJournal.isPending ? t('journalPage.exporting') : t('journalPage.exportCsv')}
              </Button>
            </div>
          </div>

            </>
          )}
        </>
      )}
    </div>
  );
}
